import React, { useCallback, useEffect, useState } from 'react';
import { Backdrop, Box, Checkbox, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DateUtils } from '../../helpers/dateUtils';
import { summarizeErrorMessages } from '../../helpers/errorUtils';
import { medicalCheckupDocumentsService } from '../../services/medicalCheckupDocumentsService';
import { medicalCheckupSigningService, NetworkError, ValidationError, WorkplaceError } from '../../services/medicalCheckupSigningService';
import { medicsService } from '../../services/medicsService';
import { cryptoProService } from '../../services/cryptoProService';
import { AwaitingWorkplaceSignatureDocumentDto, MedicalCheckupDocumentErrorDto } from '../../models/medicalCheckupDocuments/workplaceSignatureDtos';
import { TokenType } from '../../models/enums/tokenType';
import { ValidationErrorDialog } from '../common/ValidationErrorDialog';
import { WorkplaceNotReadyDialog } from '../common/WorkplaceNotReadyDialog';
import { BulkSignConfirmationDialog } from '../medical-journal/BulkSignConfirmationDialog';
import Spinner from '../common/Spinner';
import TmPencil from '../../icons/TmPencil';

const TABLE_REFRESH_INTERVAL = 10000;
const SIGN_ERROR_FALLBACK = 'Не удалось сформировать или подписать направления. Попробуйте ещё раз.';

const checkboxSx = {
    color: '#9588e8',
    '&.Mui-checked': {
        color: '#9588e8',
    },
    '&.MuiCheckbox-indeterminate': {
        color: '#9588e8',
    }
};

/**
 * Реестр «Направления на подпись»: неподписанные справки осмотров, которые завершил текущий медик.
 * Медик отмечает нужные и подписывает физическим токеном (сертификат по умолчанию).
 */
export const WorkplaceDocumentsTable = () => {
    const [data, setData] = useState<AwaitingWorkplaceSignatureDocumentDto[]>([]);
    const [dataCount, setDataCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [selected, setSelected] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [networkError, setNetworkError] = useState('');
    const [workplaceError, setWorkplaceError] = useState('');
    const [workplaceCheckLoading, setWorkplaceCheckLoading] = useState(false);
    const [documentErrors, setDocumentErrors] = useState<MedicalCheckupDocumentErrorDto[]>([]);
    const navigate = useNavigate();

    const refreshData = useCallback(() => {
        medicalCheckupDocumentsService.getAwaitingWorkplaceSignature({ page, pageSize: rowsPerPage })
            .then(r => {
                const items = r.data.data.items;
                setData(items);
                setDataCount(r.data.data.count);
                // Подписанные справки из списка уходят — снимаем с них отметку
                setSelected(prev => prev.filter(id => items.some(x => x.documentId === id)));
            })
            .catch(console.log);
    }, [page, rowsPerPage]);

    useEffect(() => {
        refreshData();
        const interval = setInterval(refreshData, TABLE_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [refreshData]);

    const handleSelect = (documentId: number) => {
        setSelected(prev => prev.includes(documentId) ? prev.filter(id => id !== documentId) : [...prev, documentId]);
    };

    const isAllSelected = data.length > 0 && data.every(x => selected.includes(x.documentId));
    const isPartiallySelected = selected.length > 0 && !isAllSelected;

    const handleSelectAll = () => {
        setSelected(isAllSelected ? [] : data.map(x => x.documentId));
    };

    const handleSign = async () => {
        setOpenConfirmDialog(false);
        if (selected.length === 0) return;

        setLoading(true);
        setLoadingText('Подписание направлений...');
        try {
            const defaultCert = await medicsService.getMedicCertificate();
            if (!defaultCert) {
                throw new WorkplaceError('Не найден действующий сертификат по умолчанию');
            }
            if (defaultCert.tokenType !== TokenType.Physical) {
                throw new WorkplaceError('Направления из этого раздела подписываются физическим токеном. Сертификат по умолчанию — облачный.');
            }

            const result = await medicalCheckupSigningService.signWorkplaceDocuments(selected, defaultCert.thumbprint);

            if (result.errors.length > 0) {
                console.error('Ошибки подписания направлений:', result.errors);
            }
            setDocumentErrors(result.errors);
            setSelected(result.errors.map(x => x.documentId));
        } catch (e: any) {
            console.error(e);
            if (e instanceof ValidationError) {
                setValidationError(summarizeErrorMessages([e.message], SIGN_ERROR_FALLBACK));
            } else if (e instanceof NetworkError) {
                setNetworkError(e.message);
            } else if (e instanceof WorkplaceError) {
                setWorkplaceError(e.message);
            } else if (e?.response) {
                setValidationError(summarizeErrorMessages([e.response?.data?.message ?? e.message], SIGN_ERROR_FALLBACK));
            } else {
                setWorkplaceError(summarizeErrorMessages([e?.message], SIGN_ERROR_FALLBACK));
            }
        } finally {
            setLoading(false);
            setLoadingText('');
            refreshData();
        }
    };

    /**
     * Одна строка на каждую причину: одинаковые ошибки по справкам не повторяются.
     */
    const getDocumentErrorsMessage = (errors: MedicalCheckupDocumentErrorDto[]) => {
        const reasons = summarizeErrorMessages(errors.map(x => x.message), SIGN_ERROR_FALLBACK).split('\n');
        return [`Не подписано направлений: ${errors.length}.`, ...reasons].join('<br/>');
    };

    return (
        <Box>
            {validationError && (
                <ValidationErrorDialog open message={validationError} onClose={() => setValidationError('')} />
            )}
            {networkError && (
                <ValidationErrorDialog open title="Ошибка связи" message={networkError} onClose={() => setNetworkError('')} />
            )}
            {documentErrors.length > 0 && (
                <ValidationErrorDialog
                    open
                    title="Не все направления подписаны"
                    message={getDocumentErrorsMessage(documentErrors)}
                    onClose={() => setDocumentErrors([])}
                />
            )}
            {workplaceError && (
                <WorkplaceNotReadyDialog
                    open
                    message={workplaceError}
                    loading={workplaceCheckLoading}
                    onClose={() => setWorkplaceError('')}
                    onRetry={async () => {
                        setWorkplaceCheckLoading(true);
                        try {
                            const wpStatus = await cryptoProService.checkWorkplaceReadiness();
                            if (wpStatus.isReady) {
                                setWorkplaceError('');
                            } else {
                                setWorkplaceError(wpStatus.errors || 'Рабочее место не готово');
                            }
                        } finally {
                            setWorkplaceCheckLoading(false);
                        }
                    }}
                />
            )}
            <div className="table-container">
                <Grid container py={1} justifyContent="flex-end">
                    <Grid item xs={2} style={{ display: 'inline-block', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <IconButton
                                sx={{
                                    display: "flex",
                                    '&.Mui-disabled': {
                                        opacity: 0.5,
                                        cursor: 'not-allowed'
                                    }
                                }}
                                className="list-item"
                                onClick={() => setOpenConfirmDialog(true)}
                                disabled={selected.length === 0 || loading}
                            >
                                <TmPencil fill="none" stroke="white" />&nbsp;
                                <Typography pr={1}>
                                    <span style={{ fontWeight: 'bold' }}>Подписать</span>
                                    {selected.length > 0 ? ` (${selected.length})` : ''}
                                </Typography>
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>

                <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 } }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={isPartiallySelected}
                                        onChange={handleSelectAll}
                                        sx={checkboxSx}
                                    />
                                    <Typography variant="body1" sx={{ color: "#7c7e92", display: 'inline', ml: 1 }}>
                                        {isAllSelected ? "Отменить выбор" : "Выбрать все"}
                                    </Typography>
                                </TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Осмотр</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Дата осмотра</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Водитель</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Клиент</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Документ</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Файл</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((item) => (
                                <TableRow key={item.documentId}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selected.includes(item.documentId)}
                                            onChange={() => handleSelect(item.documentId)}
                                            sx={checkboxSx}
                                        />
                                    </TableCell>
                                    <TableCell
                                        sx={{ fontWeight: "bold", cursor: 'pointer', color: '#9588e8' }}
                                        onClick={() => navigate(`/medical-checkups/${item.medicalCheckupId}`)}
                                    >
                                        {item.medicalCheckupId}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        {DateUtils.formatDateTimeWithTimeZone(item.medicalCheckupDate, item.timeShift)}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.driverFio}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.companyName}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.name}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.isFileReady ? 'Собран' : 'Не собран'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography>Всего: {dataCount}</Typography>
                    <TablePagination
                        rowsPerPageOptions={[15, 30, 50]}
                        component="div"
                        count={dataCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event: any, newPage: number) => setPage(newPage)}
                        onRowsPerPageChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        labelRowsPerPage={"Строк на странице:"}
                        labelDisplayedRows={({ from, to, count }: any): string => `Показано ${from}-${to} из ${count}`}
                    />
                </Box>
                <BulkSignConfirmationDialog
                    open={openConfirmDialog}
                    onClose={() => setOpenConfirmDialog(false)}
                    onConfirm={handleSign}
                    selectedCount={selected.length}
                />
            </div>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner text={loadingText} />
            </Backdrop>
        </Box>
    );
};
